// Convert a bridge-sdk-js `NearUnsignedTransaction` into the app's
// `UnsignedNearTx` DTO that the NEAR wallet (`@hot-labs/near-connect`)
// signs.
//
// The SDK always emits action `args` as a `Uint8Array` (Borsh for
// fin/deploy/bind; JSON-bytes for the UTXO/fast-fin flows) and gas/deposit
// as `bigint`. We keep `args` as the raw `Uint8Array` (NOT re-encoded to
// JSON) and stringify gas/deposit. near-connect's `nearActionsToConnectorActions`
// passes any action with a top-level `type` through unchanged, and the
// `Uint8Array` survives the wallet's postMessage boundary (structured
// clone) so the bytes reach the contract verbatim.
//
// CRITICAL invariant: the `UnsignedNearTx` this returns MUST be handed to
// the NEAR wallet IN-PROCESS (dispatchFrontend → near.signAndSend). It must
// NEVER be JSON-serialized — `JSON.stringify(Uint8Array)` yields
// `{"0":..,"1":..}` and silently corrupts the Borsh payload. That's why
// these proof flows build + sign entirely client-side and never round-trip
// the tx through the backend's JSON `UnsignedTxsResponse` (which carries
// plain-JSON args for the backend-built flows only).

import type { NearAction, UnsignedNearTx } from "@/types/dto";

/** The action shape every @omni-bridge/* builder emits. */
export interface SdkNearAction {
  type: string;
  methodName: string;
  args: Uint8Array;
  gas: bigint;
  deposit: bigint;
}

export interface SdkNearUnsignedTransaction {
  receiverId: string;
  actions: SdkNearAction[];
}

/** Convert an SDK unsigned tx to the wallet DTO. `depositOverride` replaces
 *  the per-action deposit when the builder hardcodes a placeholder (e.g.
 *  `buildFinalization` always sets ONE_YOCTO and expects the caller to
 *  compute the real storage-inclusive deposit). When omitted, the SDK's
 *  deposit is kept. */
export function sdkTxToUnsignedNearTx(
  unsigned: SdkNearUnsignedTransaction,
  depositOverride?: bigint,
): UnsignedNearTx {
  return {
    receiverId: unsigned.receiverId,
    actions: unsigned.actions.map((a): NearAction => {
      if (a.type !== "FunctionCall") {
        throw new Error(`Unexpected NEAR action type from bridge-sdk-js: ${a.type}`);
      }
      return {
        type: "FunctionCall",
        params: {
          methodName: a.methodName,
          args: decodeSdkArgs(a.args),
          gas: a.gas.toString(),
          deposit: (depositOverride ?? a.deposit).toString(),
        },
      };
    }),
  };
}

/** Normalize the SDK's `Uint8Array` action args into the form the NEAR
 *  wallet expects, mirroring near-connect's own `deserializeArgs`:
 *
 *  - JSON-method flows (UTXO submit, fast-fin) — the SDK encodes args as
 *    JSON *bytes*. Decode them to a plain object so the wallet
 *    re-serializes valid JSON. Passing the raw `Uint8Array` here is the bug
 *    that made the wallet `JSON.stringify` the byte array into
 *    `{"0":..,"1":..}` — the contract then reads a leading `{` (byte 123)
 *    and panics with `Unexpected variant tag: 123`.
 *  - Borsh-method flows (fin_transfer / deploy / bind / MPC) — the bytes
 *    aren't valid JSON, so keep the raw `Uint8Array`; a compliant
 *    near-connect wallet forwards it verbatim as the call args.
 */
function decodeSdkArgs(args: Uint8Array): unknown {
  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(args);
    const parsed = JSON.parse(text);
    // Only treat objects/arrays as JSON args; a bare number/string that
    // happens to parse is almost certainly borsh bytes, keep them raw.
    return typeof parsed === "object" && parsed !== null ? parsed : args;
  } catch {
    return args;
  }
}
